import React, { useEffect, useRef, useState } from 'react';
import { gql, useApolloClient, useLazyQuery } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/pro-regular-svg-icons/faLocationDot';
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import { faSpinner } from '@fortawesome/pro-regular-svg-icons/faSpinner';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import {
  useElementValue,
  useIsReadOnly,
} from '../DocumentView/DocumentContext';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { cn } from '../lib/utils';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';
import { FieldHeader, FieldHelpText } from './FieldParts';

interface Props {
  element: DocumentElementType;
}

interface AddressValue {
  addressLine1?: string;
  addressLine2?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  countryCode?: string;
  latitude?: number;
  longitude?: number;
  /**
   * Free-form display string of the chosen address. Populated when picked
   * from autocomplete; manually-edited values regenerate it from parts.
   */
  formatted?: string;
}

const GOOGLE_PLACES_AUTOCOMPLETE = gql`
  query googlePlacesAutocomplete(
    $query: String!
    $sessionToken: String!
    $countryCode: String
    $near: GeoPointInput
  ) {
    googlePlacesAutocomplete(
      query: $query
      sessionToken: $sessionToken
      countryCode: $countryCode
      near: $near
    ) {
      description
      placeId
    }
  }
`;

/**
 * The browser's position, only when the page already holds permission for
 * it: a lookup must never raise the permission prompt. Fetched once per
 * mount, low accuracy is plenty for ranking suggestions.
 */
function useQuietPosition(): { latitude: number; longitude: number } | null {
  const [pos, setPos] = useState<{ latitude: number; longitude: number } | null>(null);
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;
    let cancelled = false;
    const read = () =>
      navigator.geolocation.getCurrentPosition(
        (p) => {
          if (!cancelled) setPos({ latitude: p.coords.latitude, longitude: p.coords.longitude });
        },
        () => {},
        { enableHighAccuracy: false, maximumAge: 600000, timeout: 8000 },
      );
    const perms = (navigator as Navigator & { permissions?: Permissions }).permissions;
    if (perms?.query) {
      perms
        .query({ name: 'geolocation' as PermissionName })
        .then((status) => {
          if (!cancelled && status.state === 'granted') read();
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, []);
  return pos;
}

const GOOGLE_PLACE = gql`
  query googlePlace($placeId: String!, $sessionToken: String!) {
    googlePlace(placeId: $placeId, sessionToken: $sessionToken) {
      id
      addressLine1
      addressLine2
      suburb
      state
      postcode
      countryCode
      latitude
      longitude
    }
  }
`;

const formatParts = (a: AddressValue): string =>
  [a.addressLine1, a.addressLine2, a.suburb, a.state, a.postcode, a.countryCode]
    .filter((p) => p && String(p).trim() !== '')
    .join(', ');

interface ReadOnlyFieldProps {
  label: string;
  value: string | number | null | undefined;
  className?: string;
}

/**
 * Single labelled value in the viewer-mode address grid. Empty values
 * render as a muted em-dash so the grid columns stay aligned across
 * partial addresses.
 */
const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({
  label,
  value,
  className,
}) => {
  const display =
    value == null || (typeof value === 'string' && value.trim() === '')
      ? null
      : String(value);
  return (
    <div className={cn('space-y-1', className)}>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="text-sm text-foreground">
        {display ?? <span className="text-muted-foreground">—</span>}
      </p>
    </div>
  );
};

const AddressInput: React.FC<Props> = ({ element }) => {
  const [value, setValue] = useElementValue<AddressValue>(element.id);
  const isReadOnly = useIsReadOnly();
  const apollo = useApolloClient();

  const data = element.data as {
    label?: string;
    subLabel?: string;
    helpText?: string;
    placeholder?: string;
    required?: boolean;
    hidden?: boolean;
    countryCode?: string;
  };
  const label = data?.label || 'Address';
  const subLabel = data?.subLabel;
  const helpText = data?.helpText;
  const placeholder = data?.placeholder || 'Search for an address…';
  const required = data?.required || false;
  const countryCode = data?.countryCode || undefined;

  // Autocomplete state
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [sessionToken] = useState(() => uuidv4());
  const containerRef = useRef<HTMLDivElement>(null);
  const near = useQuietPosition();

  const [runAutocomplete, { data: acData, loading: acLoading }] = useLazyQuery(
    GOOGLE_PLACES_AUTOCOMPLETE,
    { fetchPolicy: 'no-cache' },
  );

  // Debounced lookup
  useEffect(() => {
    if (!query || query.length < 3 || manualMode) {
      setShowSuggestions(false);
      return;
    }
    const timer = setTimeout(() => {
      runAutocomplete({
        variables: { query, sessionToken, countryCode, near },
      });
      setShowSuggestions(true);
    }, 250);
    return () => clearTimeout(timer);
  }, [query, sessionToken, countryCode, near, manualMode, runAutocomplete]);

  // Close suggestions on outside click
  useEffect(() => {
    if (!showSuggestions) return;
    const onClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [showSuggestions]);

  const suggestions: Array<{ description: string; placeId: string }> =
    acData?.googlePlacesAutocomplete || [];

  const handleSelectSuggestion = async (placeId: string) => {
    setShowSuggestions(false);
    try {
      const result = await apollo.query({
        query: GOOGLE_PLACE,
        variables: { placeId, sessionToken },
        fetchPolicy: 'no-cache',
      });
      const place = result.data?.googlePlace;
      if (!place) return;
      const next: AddressValue = {
        addressLine1: place.addressLine1 || '',
        addressLine2: place.addressLine2 || '',
        suburb: place.suburb || '',
        state: place.state || '',
        postcode: place.postcode || '',
        countryCode: place.countryCode || '',
        latitude: place.latitude ?? undefined,
        longitude: place.longitude ?? undefined,
      };
      next.formatted = formatParts(next);
      setValue(next);
      setQuery('');
    } catch {
      // Swallow — user can fall back to manual entry
    }
  };

  const updateField = (patch: Partial<AddressValue>) => {
    const next: AddressValue = { ...(value || {}), ...patch };
    next.formatted = formatParts(next);
    next.latitude = undefined;
    next.longitude = undefined;
    setValue(next);
  };

  const clear = () => {
    setValue({} as AddressValue);
    setQuery('');
  };

  const hasAddress =
    !!value &&
    Object.values(value).some((v) => {
      if (typeof v === 'string') return v.trim() !== '';
      return v !== undefined && v !== null;
    });

  return (
    <div className="space-y-2">
      <FieldHeader label={label} subLabel={subLabel} required={required} />

      {!manualMode && !hasAddress && !isReadOnly && (
        <div className="relative" ref={containerRef}>
          <div className="relative">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="pl-9 pr-9"
              onFocus={() => {
                if (suggestions.length > 0) setShowSuggestions(true);
              }}
            />
            {acLoading && (
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                className="absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
              />
            )}
          </div>
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute z-20 mt-1 w-full rounded-md border bg-popover shadow-md">
              <ul className="max-h-64 overflow-y-auto py-1">
                {suggestions.map((s) => (
                  <li key={s.placeId}>
                    <button
                      type="button"
                      className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                      onClick={() => handleSelectSuggestion(s.placeId)}
                    >
                      <FontAwesomeIcon
                        icon={faLocationDot}
                        className="mt-0.5 h-3.5 w-3.5 text-muted-foreground"
                      />
                      <span>{s.description}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="mt-1 text-right">
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground hover:underline"
              onClick={() => setManualMode(true)}
            >
              Enter manually
            </button>
          </div>
        </div>
      )}

      {(manualMode || hasAddress) && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              Address details
            </span>
            {/* Action chrome (Edit / Clear) only makes sense when the
                user is actually filling out the form. In viewer mode we
                hide it so a reviewer sees the captured address without
                any controls that look mutable. */}
            {!isReadOnly && (
              <div className="flex items-center gap-1">
                {!manualMode && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setManualMode(true)}
                  >
                    Edit
                  </Button>
                )}
                {hasAddress && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={clear}
                  >
                    <FontAwesomeIcon icon={faXmark} className="mr-1 h-3 w-3" />
                    Clear
                  </Button>
                )}
              </div>
            )}
          </div>

          {manualMode && !isReadOnly ? (
            <div className="grid grid-cols-2 gap-2">
              <Input
                value={value?.addressLine1 || ''}
                onChange={(e) => updateField({ addressLine1: e.target.value })}
                placeholder="Address line 1"
                className="col-span-2"
              />
              <Input
                value={value?.addressLine2 || ''}
                onChange={(e) => updateField({ addressLine2: e.target.value })}
                placeholder="Address line 2 (optional)"
                className="col-span-2"
              />
              <Input
                value={value?.suburb || ''}
                onChange={(e) => updateField({ suburb: e.target.value })}
                placeholder="Suburb"
              />
              <Input
                value={value?.state || ''}
                onChange={(e) => updateField({ state: e.target.value })}
                placeholder="State"
              />
              <Input
                value={value?.postcode || ''}
                onChange={(e) => updateField({ postcode: e.target.value })}
                placeholder="Postcode"
              />
              <Input
                value={value?.countryCode || ''}
                onChange={(e) =>
                  updateField({ countryCode: e.target.value.toUpperCase() })
                }
                placeholder="Country code"
                maxLength={2}
              />
            </div>
          ) : isReadOnly ? (
            // Viewer mode — expand the captured address into its
            // labelled parts so a reviewer can see every component
            // (not just the formatted one-liner). Empty fields render
            // as a muted em-dash to keep the grid shape stable.
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              <ReadOnlyField
                label="Address line 1"
                value={value?.addressLine1}
                className="col-span-2"
              />
              {value?.addressLine2 && (
                <ReadOnlyField
                  label="Address line 2"
                  value={value.addressLine2}
                  className="col-span-2"
                />
              )}
              <ReadOnlyField label="Suburb" value={value?.suburb} />
              <ReadOnlyField label="State" value={value?.state} />
              <ReadOnlyField label="Postcode" value={value?.postcode} />
              <ReadOnlyField label="Country" value={value?.countryCode} />
              {(value?.latitude != null || value?.longitude != null) && (
                <ReadOnlyField
                  label="Coordinates"
                  value={[value.latitude, value.longitude]
                    .filter((c) => c != null)
                    .join(', ')}
                  className="col-span-2"
                />
              )}
            </div>
          ) : (
            // Captured address — wear the same "filled" treatment as a
            // completed text input (accent tint + border) so a selected
            // address reads as answered, not muted/empty.
            <div
              className={cn(
                'rounded-md border px-3 py-2 text-sm',
                'border-[var(--forms-input-filled-border)] bg-[var(--forms-input-filled-bg)]',
              )}
            >
              {value?.formatted || formatParts(value || {}) || (
                <span className="text-muted-foreground">No address yet</span>
              )}
            </div>
          )}
        </div>
      )}

      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default AddressInput;
